// Renewal around every non-auth request (FR-021, FR-023; research R5). Sits under createApiClient's
// fetch: renews before sending when the access token expires within 30 s, attaches the bearer
// token, and on a 401 renews once (shared with every concurrent caller through the controller) and
// repeats the request once with a clone (the body is sent again). A second 401 is returned as is.
// Requests to /api/v1/auth/* are public and pass through untouched.
import { isAppError } from "./errors";

export type AuthFetchSession = {
  getAccessToken(): string | null;
  getAccessTokenExpiresAt(): number | null;
  renew(): Promise<unknown>;
  /** 002 FR-046: a protected call answered 403 FORBIDDEN; renew and re-apply the roles. */
  onForbidden?(): Promise<unknown>;
};

export const RENEW_BEFORE_MS = 30_000;
const AUTH_PATH = /^\/api\/v1\/auth\//;

export function createAuthFetch({
  fetch: baseFetch,
  session,
  now = Date.now,
}: {
  fetch: (request: Request) => Promise<Response>;
  session: AuthFetchSession;
  now?: () => number;
}) {
  /** Renews; a connection failure surfaces as a network error (AppError kind `offline`). */
  async function renew(): Promise<boolean> {
    try {
      await session.renew();
      return true;
    } catch (error) {
      if (
        isAppError(error) &&
        (error.kind === "offline" || error.kind === "timeout")
      ) {
        throw new TypeError("Network request failed");
      }
      return false;
    }
  }

  function withToken(request: Request): Request {
    const token = session.getAccessToken();
    const next = new Request(request);
    if (token) next.headers.set("Authorization", `Bearer ${token}`);
    else next.headers.delete("Authorization");
    return next;
  }

  return async function authFetch(request: Request): Promise<Response> {
    if (AUTH_PATH.test(new URL(request.url).pathname))
      return baseFetch(request);

    const expiresAt = session.getAccessTokenExpiresAt();
    if (
      session.getAccessToken() &&
      expiresAt !== null &&
      expiresAt - now() <= RENEW_BEFORE_MS
    ) {
      await renew();
    }

    const retry = request.clone();
    const sentToken = session.getAccessToken();
    const response = await baseFetch(withToken(request));
    if (response.status === 403 && session.onForbidden) {
      const body = (await response
        .clone()
        .json()
        .catch(() => null)) as { code?: unknown } | null;
      if (body?.code === "FORBIDDEN")
        void session.onForbidden().catch(() => {});
      return response;
    }
    if (response.status !== 401 || !sentToken) return response;

    if (!(await renew()) || !session.getAccessToken()) return response;
    return baseFetch(withToken(retry));
  };
}
